import type { SearchClient, SearchParams } from "algoliasearch";
import includes from "lodash-es/includes";
import intersection from "lodash-es/intersection";
import { logger } from "../../common/logger";
import type { CategoryInfo, CategoryQuery, SkuAndPaths } from "../../common/types/categoryTypes";
import type { AlgoliaProductHit } from "../../common/types/searchTypes";
import productStore from "../product/productStore";
import { createProductAlgoliaFilter } from "../search/searchUtils";

export const setHiddenStatus = (cat: CategoryInfo) => {
   cat.visible = intersection(cat.skus, productStore.assortment).length > 0;
   for (const subcat of cat.children) {
      setHiddenStatus(subcat);
   }
};

/**
 * Recusively interates over the category tree and builds objects for
 * each matching category that can be used with findCategoryByUrlKey
 */
export const searchCategoryRescursive = (sku: string, cat: CategoryInfo, level: number, obj?: CategoryQuery): CategoryQuery[] => {
   if (!includes(cat.skus, sku)) {
      return [];
   }

   const newObj = {
      ...(obj ? obj : {}),
      [`cat${level}`]: cat.url_key
   };

   if (cat.children.length === 0) {
      return [newObj];
   }

   return cat.children.flatMap((subcat) => searchCategoryRescursive(sku, subcat, level + 1, newObj));
};

export const searchForCategoryProducts = async (
   viewableSkus: string[],
   algoliaClient: SearchClient,
   indexName: string,
   customerGroup: string
): Promise<SkuAndPaths[]> => {
   const filters = createProductAlgoliaFilter(viewableSkus, indexName, customerGroup);

   const searchParams: SearchParams = {
      clickAnalytics: true,
      hitsPerPage: 1000,
      filters
   };

   try {
      const response = await algoliaClient.searchSingleIndex<AlgoliaProductHit>({ indexName, searchParams });
      return response.hits.map((hit) => ({ sku: hit.sku, paths: hit.paths || [] }));
   } catch (error) {
      logger.error("categories", "Error searching for category products:", error);
   }

   return [];
};

export const updateSkusForCategory = (category: CategoryInfo, skusAndPaths: SkuAndPaths[]): void => {
   const path = category.fullUrl.replace(/\.html$/, "");
   if (path === "/produkter") {
      category.skus = skusAndPaths.map((skuAndPath) => skuAndPath.sku);
   } else {
      category.skus = skusAndPaths.filter((skuAndPath) => skuAndPath.paths.includes(path)).map((skuAndPath) => skuAndPath.sku);
   }
   if (category.children?.length) {
      for (const child of category.children) {
         updateSkusForCategory(child, skusAndPaths);
      }
   }
};
